import {
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Add, ExpandMore, GetApp, InfoOutlined, Launch, Save } from "@material-ui/icons"
import { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { alertStart } from "../../../routes"
import { RegistrarLogErro } from "../../../services/clientefiel"
import { atualizarParametrosClienteFiel } from "../../../services/configuracaoDeliveryFornecedor"
import { CERTIFICADO } from "../../../services/qzSeguranca"
import { autoAceiteLigado } from "../../../stores/ConfigsStore"
import { setForcarImpressaoNavegador } from "../../../stores/ImpressaoStore"
import useAppStore from "../../../stores/AppStore"

// Impressão automática (QZ Tray). O módulo "Cliente Fiel Printer" roda na máquina do
// cliente e imprime os pedidos sem nenhum clique. Links usados no passo a passo.
const URL_DOWNLOAD_QZ =
    "https://clientefielsp.s3-sa-east-1.amazonaws.com/Cliente+Fiel+Printer.zip"
const URL_INSTRUCOES_QZ =
    "https://ajuda.clientefiel.app/painel-cliente-fiel/ferramentas/formas-de-impressao"

const VERDE = "#4caf50"

const useStyles = makeStyles(theme => ({
    conteudo: { maxWidth: 780, margin: "0 auto" },
    subtituloTela: { color: "#7f8c8d", marginBottom: theme.spacing(2) },
    secao: {
        borderRadius: 16,
        border: "1px solid #e9ecef",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(2.5),
    },
    secaoHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(1.5, 2),
        borderBottom: "1px solid #e9ecef",
    },
    secaoTitulo: { fontWeight: 600, color: "#2c3e50" },
    secaoBody: { padding: theme.spacing(2.5) },
    campoLabelLinha: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        marginBottom: 6,
        minHeight: 24,
    },
    campoLabel: { fontWeight: 500, color: "#2c3e50" },
    campo: {
        backgroundColor: "white",
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: VERDE },
            "&.Mui-focused fieldset": { borderColor: VERDE },
        },
    },
    linhaSwitch: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: theme.spacing(0.75, 0),
        borderBottom: "1px solid #f1f3f5",
        "&:last-child": { borderBottom: "none" },
    },
    linhaSwitchLabel: { color: "#2c3e50" },
    switchVerde: {
        "& .Mui-checked": { color: VERDE },
        "& .Mui-checked + .MuiSwitch-track": { backgroundColor: VERDE },
    },
    botaoSecundario: {
        borderRadius: 12,
        textTransform: "none",
        borderColor: "#c9ccd1",
        color: "#2c3e50",
    },
    botaoImprimirAuto: {
        backgroundColor: "#6c757d",
        color: "white",
        textTransform: "none",
        marginTop: theme.spacing(1),
    },
    // Passo a passo do diálogo "Impressão automática (QZ)"
    passo: {
        display: "flex",
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
    },
    passoNumero: {
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "50%",
        backgroundColor: VERDE,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.9rem",
    },
    passoTitulo: { fontWeight: 600, color: "#2c3e50", marginBottom: 2 },
    passoTexto: { color: "#5a6570", fontSize: "0.9rem", lineHeight: 1.5 },
    passoLista: {
        margin: "6px 0 0",
        paddingLeft: 18,
        color: "#5a6570",
        fontSize: "0.9rem",
        lineHeight: 1.6,
    },
    botaoAcaoInstrucao: {
        textTransform: "none",
        marginTop: theme.spacing(1),
        borderRadius: 10,
    },
    avisoCert: {
        backgroundColor: "#fff8e1",
        border: "1px solid #ffe0a3",
        borderRadius: 10,
        padding: theme.spacing(1.5),
        color: "#7a5c00",
        fontSize: "0.85rem",
        lineHeight: 1.5,
        marginTop: theme.spacing(1),
    },
    avancadoHeader: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        padding: theme.spacing(1.5, 0),
        color: "#2c3e50",
        fontWeight: 600,
        userSelect: "none",
    },
    seta: { transition: "transform 0.2s", fontSize: "1.4rem" },
    setaAberta: { transform: "rotate(180deg)" },
    botaoSalvar: {
        borderRadius: 25,
        backgroundColor: VERDE,
        color: "white",
        padding: theme.spacing(1, 3),
        fontWeight: 600,
        textTransform: "none",
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        "&:hover": { backgroundColor: "#45a049" },
    },
    botaoVoltar: { textTransform: "none", color: "#6c757d" },
}))

// Rótulo acima do campo. Quando há `legenda`, um ícone "i" ao lado exibe a descrição
// só ao ser clicado (economiza espaço).
function Campo({ label, legenda, children }) {
    const classes = useStyles()
    const [ajudaAberta, setAjudaAberta] = useState(false)
    return (
        <>
            <div className={classes.campoLabelLinha}>
                <Typography variant="body2" className={classes.campoLabel}>
                    {label}
                </Typography>
                {legenda && (
                    <Tooltip
                        arrow
                        title={legenda}
                        open={ajudaAberta}
                        onClose={() => setAjudaAberta(false)}
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                    >
                        <IconButton
                            size="small"
                            style={{ padding: 2 }}
                            onClick={() => setAjudaAberta(a => !a)}
                        >
                            <InfoOutlined style={{ fontSize: "1rem", color: "#7f8c8d" }} />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            {children}
        </>
    )
}

// Card de seção com cabeçalho.
function Secao({ titulo, children }) {
    const classes = useStyles()
    return (
        <div className={classes.secao}>
            <div className={classes.secaoHeader}>
                <Typography variant="subtitle1" className={classes.secaoTitulo}>
                    {titulo}
                </Typography>
            </div>
            <div className={classes.secaoBody}>{children}</div>
        </div>
    )
}

// Linha com rótulo à esquerda e switch à direita. Aceita `checked` (controlado) OU
// `defaultChecked` (não controlado), preservando o comportamento original de cada campo.
function LinhaSwitch({ label, checked, defaultChecked, onChange, name, disabled }) {
    const classes = useStyles()
    return (
        <div className={classes.linhaSwitch}>
            <Typography variant="body2" className={classes.linhaSwitchLabel}>
                {label}
            </Typography>
            <Switch
                className={classes.switchVerde}
                {...(defaultChecked !== undefined ? { defaultChecked } : { checked })}
                onChange={onChange}
                disabled={disabled}
                name={name}
            />
        </div>
    )
}

const LIMITE_NUMERO_IMPRESSORAS = 10

function getImpressoras(configuracoes) {
    if (configuracoes.printer.impressoras?.length) return configuracoes.printer.impressoras
    return []
}

// Seleção de impressoras do QZ (apenas Plugin/CRUD, fora do mobile). Renderiza dentro
// da seção "Impressoras" do novo layout.
function ListaImpressorasSelecionadas({ mobile, tipoPainelImpressao, impressorasQZ }) {
    const classes = useStyles()
    const tabStatus = useAppStore(s => s.tabStatus)
    const configuracoes = useAppStore(s => s.configuracoes)
    const atualizarConfiguracoes = useAppStore(s => s.atualizarConfiguracoes)

    const [listaImpressoras, setListaImpressoras] = useState(getImpressoras(configuracoes))

    const handleChangeImpressora = (e, i) => {
        listaImpressoras[i] = e.target.value
        const impressorasFiltradas = listaImpressoras.filter(
            i => i && impressorasQZ.find(nomeImpressora => nomeImpressora === i)
        )
        setListaImpressoras(impressorasFiltradas)
        atualizarConfiguracoes({ printer: { impressoras: impressorasFiltradas } })
    }

    if (tipoPainelImpressao !== "plugin" || mobile || tabStatus !== "CRUD") return null

    const adicionarImpressora = () => setListaImpressoras(prev => [...prev, "Nenhuma"])

    return (
        <>
            <Grid container spacing={2}>
                {listaImpressoras?.map((impressora, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                        <Campo label={`Impressora ${i + 1}`}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                className={classes.campo}
                                fullWidth
                            >
                                <Select
                                    fullWidth
                                    value={impressora}
                                    onChange={e => handleChangeImpressora(e, i)}
                                >
                                    <MenuItem value="Nenhuma">Nenhuma</MenuItem>
                                    {impressorasQZ.map((printer, index) => (
                                        <MenuItem key={index} value={printer}>
                                            {printer}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Campo>
                    </Grid>
                ))}
            </Grid>

            {listaImpressoras.length < LIMITE_NUMERO_IMPRESSORAS ? (
                <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={adicionarImpressora}
                    className={classes.botaoSecundario}
                    style={{ marginTop: 12 }}
                >
                    Adicionar impressora
                </Button>
            ) : null}

            {configuracoes?.user?.parametros?.impressaoAlternada ? (
                <Grid container spacing={2} style={{ marginTop: 4 }}>
                    <Grid item xs={12} sm={6}>
                        <LinhaSwitch
                            label="Imprimir comanda completa"
                            checked={configuracoes.printer.imprimirComandaCompleta}
                            onChange={e =>
                                atualizarConfiguracoes({
                                    printer: { imprimirComandaCompleta: e.target.checked },
                                })
                            }
                        />
                    </Grid>
                    {configuracoes.printer.imprimirComandaCompleta ? (
                        <Grid item xs={12} sm={6}>
                            <Campo label="Impressora p/ comanda completa">
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.campo}
                                    fullWidth
                                >
                                    <Select
                                        fullWidth
                                        value={configuracoes.printer.impressoraComandaCompleta}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                printer: {
                                                    impressoraComandaCompleta:
                                                        e.target.value !== "Nenhuma"
                                                            ? e.target.value
                                                            : null,
                                                },
                                            })
                                        }
                                    >
                                        <MenuItem value="Nenhuma">Nenhuma</MenuItem>
                                        {impressorasQZ.map((printer, index) => (
                                            <MenuItem key={index} value={printer}>
                                                {printer}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Campo>
                        </Grid>
                    ) : null}
                </Grid>
            ) : null}
        </>
    )
}

/**
 * Tela de configuração de impressão (usada no popup do Recebedor e reutilizável em
 * outros pontos do sistema). Renderiza DialogTitle/Content/Actions — o chamador
 * apenas envolve em <Dialog>. `onFechar` fecha o diálogo.
 */
export default function ConfiguracaoImpressao({ onFechar }) {
    const classes = useStyles()
    const history = useHistory()

    const configuracoes = useAppStore(s => s.configuracoes)
    const atualizarConfiguracoes = useAppStore(s => s.atualizarConfiguracoes)
    const impressorasQZ = useAppStore(s => s.impressorasQZ)
    const tabStatus = useAppStore(s => s.tabStatus)

    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    )

    const { user, printer, recebedor } = configuracoes
    const { parametros } = user

    const imprimirPedidoMesa = recebedor.imprimirPedidoMesa ?? true

    // O tipo da impressão NÃO é escolhido pelo usuário: fica travado pela presença do QZ.
    // Com QZ conectado → "QZ (Automática)"; sem QZ → "Navegador (Manual)". O informativo de
    // impressão automática (download do QZ + certificado) fica sempre disponível no modo manual.
    const qzConectado = impressorasQZ.length > 0
    const tipoPainelImpressao = qzConectado
        ? "plugin"
        : window.navigator.userAgent.includes("Electron")
        ? "software"
        : "navegador"

    // Como o override manual foi removido, garante que uma flag antiga em localStorage não
    // mantenha a estação em "navegador" com o QZ conectado (ficaria travado na automática).
    useEffect(() => {
        setForcarImpressaoNavegador(false)
    }, [])

    const tipoPrinter =
        tipoPainelImpressao === "plugin"
            ? printer.tipoImpressao
                ? printer.tipoImpressao
                : "code"
            : ""
    const tamanhoFonteSelect = printer.tamanhoFonteSelect ? printer.tamanhoFonteSelect : "media"

    const exibirTamanhoFonte = tipoPrinter === "html"
    const exibirImprimirLogo = tipoPrinter === "code"
    const exibirNegrito = tipoPrinter === "code"
    const exibir99food = tipoPrinter === "code" || tipoPrinter === "html"

    const softwareDisponivel = window.navigator.userAgent.includes("Electron")
    // "Imprimir comandas do iFood" aparece sempre que houver integração iFood ativa,
    // independentemente do modo de impressão (navegador/QZ).
    const integradoComIfood = parametros?.ativaIntegracaoIfood
    const mostrarSecaoImpressoras =
        !mobile && tabStatus === "CRUD" && tipoPainelImpressao === "plugin"
    const mostrarComportamentoAuto =
        !mobile && tabStatus === "CRUD" && autoAceiteLigado(recebedor)

    const [avancadoAberto, setAvancadoAberto] = useState(false)
    const [dialogInstrucoesQz, setDialogInstrucoesQz] = useState(false)

    // Baixa o certificado do Cliente Fiel (arquivo .crt) para o cliente importar no QZ
    // (Site Manager), eliminando o aviso "Allow/Block" a cada impressão.
    const baixarCertificado = () => {
        try {
            const blob = new Blob([CERTIFICADO], { type: "application/x-x509-ca-cert" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = "cliente-fiel-qz.crt"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (e) {
            RegistrarLogErro(e, "baixarCertificadoQz", "")
        }
    }

    // Campo "Tamanho" contextual (varia conforme o tipo de impressão).
    const campoTamanho = () => {
        if (tipoPainelImpressao === "navegador") {
            return (
                <FormControl variant="outlined" size="small" className={classes.campo} fullWidth>
                    <Select
                        value={printer.tamanhoFonteNavegador ?? "normal"}
                        onChange={e =>
                            atualizarConfiguracoes({
                                printer: { tamanhoFonteNavegador: e.target.value },
                            })
                        }
                    >
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="grande">Grande</MenuItem>
                    </Select>
                </FormControl>
            )
        }
        if (exibirTamanhoFonte) {
            return (
                <TextField
                    className={classes.campo}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="14"
                    value={printer.tamanhoFonteImpressao}
                    onChange={e =>
                        atualizarConfiguracoes({
                            printer: { tamanhoFonteImpressao: e.target.value },
                        })
                    }
                />
            )
        }
        return (
            <FormControl variant="outlined" size="small" className={classes.campo} fullWidth>
                <Select
                    value={tamanhoFonteSelect}
                    onChange={e =>
                        atualizarConfiguracoes({
                            printer: { tamanhoFonteSelect: e.target.value },
                        })
                    }
                >
                    <MenuItem value="pequena">Pequena</MenuItem>
                    <MenuItem value="media">Média</MenuItem>
                    <MenuItem value="grande">Grande</MenuItem>
                </Select>
            </FormControl>
        )
    }

    const salvar = async () => {
        try {
            const auth = localStorage.getItem("chaveSistema")
            const resp = await atualizarParametrosClienteFiel(auth, {
                viasImpressao: parametros.viasImpressao,
                colunasPorLinhaImpressao: parametros.colunasPorLinhaImpressao,
                textoInicioLinha: parametros.textoInicioLinha,
            })
            if (resp?.retornoErro) {
                alertStart(resp.mensagem, "error")
                return
            }
            alertStart("Configuração de impressão salva.", "success")
            onFechar?.()
        } catch (error) {
            RegistrarLogErro(error, "salvarConfigImpressao")
            alertStart("Erro ao salvar configuração de impressão.", "error")
        }
    }

    return (
        <>
            <Dialog
                open={dialogInstrucoesQz}
                onClose={() => setDialogInstrucoesQz(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={mobile}
            >
                <DialogTitle>Ativar impressão automática (QZ)</DialogTitle>
                <DialogContent dividers>
                    <Typography className={classes.passoTexto} style={{ marginBottom: 20 }}>
                        A impressão automática usa o <strong>Cliente Fiel Printer (QZ Tray)</strong>
                        , um pequeno programa que roda no seu computador e imprime os pedidos
                        <strong> sozinho, sem nenhum clique</strong>. Siga os passos abaixo uma única
                        vez para configurar.
                    </Typography>

                    {/* Passo 1 */}
                    <div className={classes.passo}>
                        <div className={classes.passoNumero}>1</div>
                        <div>
                            <div className={classes.passoTitulo}>
                                Baixar e instalar o Cliente Fiel Printer
                            </div>
                            <div className={classes.passoTexto}>
                                Baixe o instalador, execute e conclua a instalação (Avançar →
                                Avançar → Concluir). Ele funciona no Windows.
                            </div>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                className={classes.botaoAcaoInstrucao}
                                startIcon={<GetApp />}
                                href={URL_DOWNLOAD_QZ}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Baixar Cliente Fiel Printer
                            </Button>
                        </div>
                    </div>

                    {/* Passo 2 */}
                    <div className={classes.passo}>
                        <div className={classes.passoNumero}>2</div>
                        <div>
                            <div className={classes.passoTitulo}>Deixar o programa aberto</div>
                            <div className={classes.passoTexto}>
                                Após instalar, o ícone do QZ aparece na{" "}
                                <strong>barra de tarefas, ao lado do relógio</strong> (bandeja do
                                sistema). Ele precisa ficar <strong>sempre aberto</strong> enquanto
                                você usa o painel — deixe-o iniciar junto com o computador.
                            </div>
                        </div>
                    </div>

                    {/* Passo 3 */}
                    <div className={classes.passo}>
                        <div className={classes.passoNumero}>3</div>
                        <div>
                            <div className={classes.passoTitulo}>
                                Adicionar o certificado ao QZ
                            </div>
                            <div className={classes.passoTexto}>
                                Isso faz o QZ confiar no Cliente Fiel e imprimir{" "}
                                <strong>sem pedir permissão</strong> a cada pedido. Baixe o
                                certificado e importe no QZ:
                            </div>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.botaoAcaoInstrucao}
                                startIcon={<GetApp />}
                                onClick={baixarCertificado}
                            >
                                Baixar certificado
                            </Button>
                            <ol className={classes.passoLista}>
                                <li>
                                    Clique com o <strong>botão direito</strong> no ícone do QZ (na
                                    bandeja, ao lado do relógio).
                                </li>
                                <li>
                                    Abra <strong>Advanced</strong> (Avançado) →{" "}
                                    <strong>Site Manager</strong> (Gerenciador de Sites).
                                </li>
                                <li>
                                    Na aba <strong>Sites</strong>, clique em{" "}
                                    <strong>“+” / Import</strong> e selecione o arquivo{" "}
                                    <strong>cliente-fiel-qz.crt</strong> que você baixou.
                                </li>
                                <li>
                                    Confirme para o Cliente Fiel ficar como{" "}
                                    <strong>“Allowed” (Permitido)</strong>.
                                </li>
                            </ol>
                            <div className={classes.avisoCert}>
                                Se aparecer o aviso <strong>“Allow / Block”</strong> ao imprimir,
                                marque <strong>“Remember this decision”</strong> e clique em{" "}
                                <strong>Allow</strong> — assim ele não pergunta mais.
                            </div>
                        </div>
                    </div>

                    {/* Passo 4 */}
                    <div className={classes.passo}>
                        <div className={classes.passoNumero}>4</div>
                        <div>
                            <div className={classes.passoTitulo}>
                                Selecionar a impressora e ativar o modo automático
                            </div>
                            <div className={classes.passoTexto}>
                                Com o QZ aberto, volte a esta tela: o{" "}
                                <strong>“Tipo da Impressão”</strong> muda para{" "}
                                <strong>“QZ (Automática)”</strong> sozinho assim que a conexão é
                                detectada. Selecione a impressora na seção{" "}
                                <strong>Impressoras</strong>. Depois, no topo do painel, em{" "}
                                <strong>“Operação”</strong>, escolha{" "}
                                <strong>“Operação automática”</strong>.
                            </div>
                        </div>
                    </div>

                    <Button
                        size="small"
                        className={classes.botaoAcaoInstrucao}
                        startIcon={<Launch />}
                        href={URL_INSTRUCOES_QZ}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textTransform: "none" }}
                    >
                        Ver instruções completas com imagens
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogInstrucoesQz(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <DialogContent style={{ paddingTop: 24 }}>
                <div className={classes.conteudo}>
                    {/* 1. Configuração principal */}
                    <Secao titulo="Configuração principal">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Campo label="Tipo da Impressão">
                                    <FormControl
                                        variant="outlined"
                                        size="small"
                                        className={classes.campo}
                                        fullWidth
                                    >
                                        <Select fullWidth value={tipoPainelImpressao} disabled>
                                            <MenuItem value="navegador">Navegador (Manual)</MenuItem>
                                            <MenuItem value="plugin">QZ (Automática)</MenuItem>
                                            {softwareDisponivel && (
                                                <MenuItem value="software">Software EXE</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: "#7f8c8d",
                                            display: "block",
                                            marginTop: 4,
                                        }}
                                    >
                                        {qzConectado
                                            ? "QZ conectado: impressão automática ativa."
                                            : "Sem QZ conectado: impressão manual pelo navegador. Instale o QZ e o certificado para ativar a automática."}
                                    </Typography>
                                </Campo>
                                {tipoPainelImpressao !== "plugin" && (
                                    <Button
                                        variant="contained"
                                        className={classes.botaoImprimirAuto}
                                        onClick={() => setDialogInstrucoesQz(true)}
                                    >
                                        Imprimir Comanda Automaticamente
                                    </Button>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Campo label="Número de vias">
                                    <TextField
                                        className={classes.campo}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        value={parametros.viasImpressao ?? ""}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                user: {
                                                    parametros: {
                                                        viasImpressao:
                                                            e.target.value === ""
                                                                ? null
                                                                : Number(e.target.value),
                                                    },
                                                },
                                            })
                                        }
                                    />
                                </Campo>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Campo label="Largura da impressão">
                                    <TextField
                                        className={classes.campo}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        value={parametros.colunasPorLinhaImpressao ?? ""}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                user: {
                                                    parametros: {
                                                        colunasPorLinhaImpressao:
                                                            e.target.value === ""
                                                                ? null
                                                                : Number(e.target.value),
                                                    },
                                                },
                                            })
                                        }
                                    />
                                </Campo>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Campo label="Tamanho">{campoTamanho()}</Campo>
                            </Grid>
                        </Grid>

                        <Button
                            variant="outlined"
                            startIcon={<Launch />}
                            className={classes.botaoSecundario}
                            style={{ marginTop: 16 }}
                            onClick={() => {
                                onFechar?.()
                                history.push("/servicos/modulo/texto-impressao")
                            }}
                        >
                            Personalizar texto da comanda
                        </Button>
                    </Secao>

                    {/* 2. Impressoras */}
                    {mostrarSecaoImpressoras && (
                        <Secao titulo="Impressoras">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Campo label="Modo de impressão">
                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            className={classes.campo}
                                            fullWidth
                                        >
                                            <Select
                                                fullWidth
                                                value={tipoPrinter}
                                                onChange={e =>
                                                    atualizarConfiguracoes({
                                                        printer: {
                                                            tipoImpressao: e.target.value,
                                                        },
                                                    })
                                                }
                                            >
                                                <MenuItem value="code">Tipo CODE</MenuItem>
                                                <MenuItem value="html">Tipo HTML</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Campo>
                                </Grid>
                            </Grid>

                            <ListaImpressorasSelecionadas
                                mobile={mobile}
                                tipoPainelImpressao={tipoPainelImpressao}
                                impressorasQZ={impressorasQZ}
                            />
                        </Secao>
                    )}

                    {/* 3. Comportamento */}
                    <Secao titulo="Comportamento">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Campo label="Máximo de Pedidos Visíveis">
                                    <TextField
                                        className={classes.campo}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        defaultValue={recebedor.exibicaoMaxima}
                                        placeholder="30"
                                        onChange={e => {
                                            if (e.target.value >= 10) {
                                                atualizarConfiguracoes({
                                                    recebedor: { exibicaoMaxima: e.target.value },
                                                })
                                            } else {
                                                alertStart(
                                                    "A exibição mínima é de 10 pedidos",
                                                    "error"
                                                )
                                            }
                                        }}
                                    />
                                </Campo>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Campo label="Máximo de pedidos finalizados exibidos">
                                    <TextField
                                        className={classes.campo}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={recebedor.limitePedidosFinalizados}
                                        onChange={e => {
                                            const numberLimite = Number(
                                                e.target.value.replace(/\D/g, "")
                                            )
                                            if (!isNaN(numberLimite)) {
                                                atualizarConfiguracoes({
                                                    recebedor: {
                                                        limitePedidosFinalizados: numberLimite,
                                                    },
                                                })
                                            }
                                        }}
                                    />
                                </Campo>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Campo
                                    label="Intervalo de atualização (segundos)"
                                    legenda="Com que frequência o painel consulta os pedidos no servidor como rede de segurança, caso um aviso em tempo real (SSE) se perca. Menor = o pedido aparece e é aceito mais rápido, mas gera mais consultas. Configuração por estação (este navegador); passa a valer ao recarregar a página. Mínimo de 15 segundos; padrão 60."
                                >
                                    <TextField
                                        className={classes.campo}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        defaultValue={
                                            localStorage.getItem("intervaloRedeSegurancaSeg") || 60
                                        }
                                        placeholder="60"
                                        inputProps={{ min: 15 }}
                                        onChange={e => {
                                            const bruto = String(e.target.value).trim()
                                            if (bruto === "") {
                                                localStorage.removeItem("intervaloRedeSegurancaSeg")
                                                return
                                            }
                                            const seg = Number(bruto.replace(/\D/g, ""))
                                            if (!Number.isFinite(seg) || seg < 15) {
                                                alertStart(
                                                    "O intervalo mínimo é de 15 segundos",
                                                    "error"
                                                )
                                                return
                                            }
                                            localStorage.setItem(
                                                "intervaloRedeSegurancaSeg",
                                                String(seg)
                                            )
                                        }}
                                    />
                                </Campo>
                            </Grid>
                        </Grid>

                        <div style={{ marginTop: 8 }}>
                            {mostrarComportamentoAuto && (
                                <>
                                    {/* "Preparar pedidos automaticamente" foi movido para o
                                        seletor de Operação (Cabeçalho): só faz sentido junto da
                                        escolha do modo automático, e lá fica sempre visível. */}
                                    <LinhaSwitch
                                        label="Imprimir pedidos de mesa"
                                        defaultChecked={imprimirPedidoMesa}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                recebedor: {
                                                    prepararPedidoAutomatico: e.target.checked,
                                                    imprimirPedidoMesa:
                                                        !configuracoes.recebedor.imprimirPedidoMesa,
                                                },
                                            })
                                        }
                                        name="imprimir-pedido-mesa"
                                    />
                                    {tipoPainelImpressao === "navegador" && (
                                        <LinhaSwitch
                                            label="Imprimir pedidos automaticamente"
                                            defaultChecked={recebedor.imprimirPedidoAutomatico}
                                            onChange={e =>
                                                atualizarConfiguracoes({
                                                    recebedor: {
                                                        imprimirPedidoAutomatico: e.target.checked,
                                                    },
                                                })
                                            }
                                            name="imprimir-automatico"
                                        />
                                    )}
                                </>
                            )}
                            {exibir99food && (
                                <LinhaSwitch
                                    label="Imprimir comandas da 99Food"
                                    defaultChecked={printer.imprimir99automatico}
                                    onChange={e => {
                                        atualizarConfiguracoes({
                                            printer: { imprimir99automatico: e.target.checked },
                                        })
                                        window.setCookie?.("imprimir-99", e.target.checked, 9999)
                                    }}
                                    name="imprimir99automatico"
                                />
                            )}
                            {integradoComIfood && (
                                <LinhaSwitch
                                    label="Imprimir comandas do iFood"
                                    checked={printer.imprimirComandasIfood ?? true}
                                    onChange={e =>
                                        atualizarConfiguracoes({
                                            printer: { imprimirComandasIfood: e.target.checked },
                                        })
                                    }
                                    name="imprimir-ifood"
                                />
                            )}
                        </div>
                    </Secao>

                    {/* 4. Exibição das informações */}
                    <Secao titulo="Exibição das informações">
                        <Grid container spacing={2}>
                            {exibirImprimirLogo && (
                                <Grid item xs={12} sm={6}>
                                    <LinhaSwitch
                                        label="Imprimir logo"
                                        defaultChecked={printer.imprimirLogo}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                printer: { imprimirLogo: e.target.checked },
                                            })
                                        }
                                        name="imprimir-logo"
                                    />
                                </Grid>
                            )}
                            {exibirNegrito && (
                                <Grid item xs={12} sm={6}>
                                    <LinhaSwitch
                                        label="Texto em negrito"
                                        defaultChecked={printer.negrito}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                printer: { negrito: e.target.checked },
                                            })
                                        }
                                        name="Negrito"
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <LinhaSwitch
                                    label="Mostrar endereço"
                                    defaultChecked={recebedor.mostrarEndereco}
                                    onChange={e =>
                                        atualizarConfiguracoes({
                                            recebedor: { mostrarEndereco: e.target.checked },
                                        })
                                    }
                                    name="mostrar-endereco"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LinhaSwitch
                                    label="Mostrar entregador"
                                    defaultChecked={recebedor.mostrarEntregador}
                                    onChange={e =>
                                        atualizarConfiguracoes({
                                            recebedor: { mostrarEntregador: e.target.checked },
                                        })
                                    }
                                    name="mostrar-entregador"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LinhaSwitch
                                    label="Exibir tempo de espera"
                                    defaultChecked={recebedor.exibirTempoEspera}
                                    onChange={e =>
                                        atualizarConfiguracoes({
                                            recebedor: { exibirTempoEspera: e.target.checked },
                                        })
                                    }
                                    name="tempo-de-espera"
                                />
                            </Grid>
                        </Grid>
                    </Secao>

                    {/* 5. Alertas */}
                    <Secao titulo="Alertas">
                        <LinhaSwitch
                            label="Efeito sonoro"
                            checked={recebedor.efeitoSonoro}
                            onChange={e => {
                                atualizarConfiguracoes({
                                    recebedor: { efeitoSonoro: e.target.checked },
                                })
                                window.setCookie?.("efeito-sonoro-ativo", e.target.checked, 9999)
                            }}
                            name="efeito-sonoro"
                        />
                        <div style={{ marginTop: 12, maxWidth: 260 }}>
                            <Campo label="Duração do alerta sonoro">
                                <TextField
                                    className={classes.campo}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={recebedor.duracaoEfeitoSonoro}
                                    disabled={!recebedor.efeitoSonoro}
                                    onChange={e =>
                                        atualizarConfiguracoes({
                                            recebedor: {
                                                duracaoEfeitoSonoro: e.target.value.replace(
                                                    /\D/g,
                                                    ""
                                                ),
                                            },
                                        })
                                    }
                                />
                            </Campo>
                        </div>
                    </Secao>

                    {/* 6. Configurações avançadas (recolhível) */}
                    <div
                        className={classes.avancadoHeader}
                        onClick={() => setAvancadoAberto(a => !a)}
                    >
                        <ExpandMore
                            className={`${classes.seta} ${
                                avancadoAberto ? classes.setaAberta : ""
                            }`}
                        />
                        Configurações avançadas
                    </div>
                    <Collapse in={avancadoAberto}>
                        <div className={classes.secao}>
                            <div className={classes.secaoBody} style={{ maxWidth: 360 }}>
                                <Campo
                                    label="Ajuste do início da linha"
                                    legenda="Texto impresso no começo de cada linha da comanda (ex.: um espaço ou caractere de recuo)."
                                >
                                    <TextField
                                        className={classes.campo}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={parametros.textoInicioLinha ?? ""}
                                        onChange={e =>
                                            atualizarConfiguracoes({
                                                user: {
                                                    parametros: {
                                                        textoInicioLinha: e.target.value,
                                                    },
                                                },
                                            })
                                        }
                                    />
                                </Campo>
                            </div>
                        </div>
                    </Collapse>
                </div>
            </DialogContent>

            <Divider />
            <DialogActions style={{ padding: "12px 24px" }}>
                <Button className={classes.botaoVoltar} onClick={() => onFechar?.()}>
                    Voltar
                </Button>
                <Button
                    variant="contained"
                    className={classes.botaoSalvar}
                    startIcon={<Save />}
                    onClick={salvar}
                >
                    Salvar
                </Button>
            </DialogActions>
        </>
    )
}
